import {announce} from '@github-ui/aria-live'
import {WorkflowDispatchRefType} from '@github-ui/dotcom-schema/types/actions/v1/workflow_dispatch_definition'
import type {RefType} from '@github-ui/ref-selector'
import {useAnalytics} from '@github-ui/use-analytics'
import {Button, Tooltip} from '@primer/react'
import {useCallback, useRef, useState} from 'react'

import {useWorkflowDispatchDefinition} from '../hooks/use-workflow-dispatch-definition'
import {WorkflowDispatchDialog} from './WorkflowDispatchDialog'

interface WorkflowDispatchMenuProps {
  owner: string
  repo: string
  workflowPath: string
}

function protoRefTypeToRefType(type?: WorkflowDispatchRefType): RefType {
  return type === WorkflowDispatchRefType.TAG ? 'tag' : 'branch'
}

/** Renders the "Run workflow" action button and loads its dispatch definition on demand. */
export function WorkflowDispatchMenu({owner, repo, workflowPath}: WorkflowDispatchMenuProps) {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedRef, setSelectedRef] = useState('')
  const [selectedRefType, setSelectedRefType] = useState<RefType>('branch')
  const {sendAnalyticsEvent} = useAnalytics({optional: true})

  const definitionQuery = useWorkflowDispatchDefinition({
    owner,
    repo,
    workflowPath,
    selectedRef,
    selectedRefType,
    enabled: isOpen,
    onFetchError: () => {
      announce('Workflow details could not be loaded. Try loading the workflow details again.')
    },
    onFetchSuccess: definition => {
      if (!definition.workflowValid) {
        announce(
          `Workflow dispatch unavailable. Workflow does not exist or does not have a workflow_dispatch trigger in this ${protoRefTypeToRefType(definition.selectedRefType)}.`,
        )
      }
    },
  })
  const refetchDefinition = definitionQuery.refetch

  const sendInteraction = useCallback(
    (action: string, label: string) => {
      sendAnalyticsEvent('analytics.click', undefined, {
        category: 'ACTIONS_WORKFLOW_DISPATCH',
        action,
        label,
      })
    },
    [sendAnalyticsEvent],
  )

  const retryDefinition = useCallback(() => {
    sendInteraction('workflow_dispatch.retry_definition', 'ref_loc:workflow_dispatch_dialog')
    void refetchDefinition()
  }, [refetchDefinition, sendInteraction])

  const openDialog = () => {
    setIsOpen(true)
    sendInteraction('workflow_dispatch.open', 'ref_loc:actions_workflow_header')
  }

  const definition = definitionQuery.data
  const effectiveSelectedRef = selectedRef || definition?.selectedRef || definition?.defaultRef || ''
  const effectiveSelectedRefType = selectedRef
    ? selectedRefType
    : protoRefTypeToRefType(definition?.selectedRefType || definition?.defaultRefType)

  return (
    <>
      <Tooltip text="This workflow has a workflow_dispatch event trigger.">
        <Button ref={triggerRef} variant="primary" onClick={openDialog}>
          Run workflow
        </Button>
      </Tooltip>
      <WorkflowDispatchDialog
        definition={definition}
        error={definitionQuery.isError}
        isFetching={definitionQuery.isPending || definitionQuery.isFetching}
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false)
          sendInteraction('workflow_dispatch.close', 'ref_loc:workflow_dispatch_dialog')
        }}
        onRefSelectorOpenChange={open => {
          if (open) sendInteraction('workflow_dispatch.open_ref_selector', 'ref_loc:workflow_dispatch_dialog')
        }}
        onRetry={retryDefinition}
        onSelectRef={(ref, refType) => {
          setSelectedRef(ref)
          setSelectedRefType(refType)
          sendInteraction('workflow_dispatch.select_ref', `ref_loc:workflow_dispatch_dialog;ref_type:${refType}`)
        }}
        onSubmit={() => {
          sendInteraction('workflow_dispatch.submit', 'ref_loc:workflow_dispatch_dialog')
        }}
        owner={owner}
        repo={repo}
        returnFocusRef={triggerRef}
        selectedRef={effectiveSelectedRef}
        selectedRefType={effectiveSelectedRefType}
      />
    </>
  )
}
